import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export type VisionRFPParameters = {
  coverageTypes: string[];
  materialsCopay: string;
  customCopayAmount?: number;
  benefitsFrequency: string;
  allowanceAmount: number;
};

type VisionRFPFormProps = {
  value: VisionRFPParameters;
  onChange: (value: VisionRFPParameters) => void;
};

export function VisionRFPForm({ value, onChange }: VisionRFPFormProps) {
  const updateField = (field: keyof VisionRFPParameters, newValue: any) => {
    onChange({ ...value, [field]: newValue });
  };

  const toggleCoverageType = (type: string) => {
    const current = value.coverageTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    updateField('coverageTypes', updated);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CoverageIcon planType="vision" size="sm" />
          Vision
        </CardTitle>
        <p className="text-xs text-muted-foreground">Exam and materials cover, and how often it renews.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Coverage Type */}
        <div className="space-y-3">
          <Label>Coverage Type</Label>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vision-employer"
                checked={value.coverageTypes?.includes('employer_sponsored')}
                onCheckedChange={() => toggleCoverageType('employer_sponsored')}
              />
              <label htmlFor="vision-employer" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Employer Sponsored
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="vision-voluntary"
                checked={value.coverageTypes?.includes('voluntary')}
                onCheckedChange={() => toggleCoverageType('voluntary')}
              />
              <label htmlFor="vision-voluntary" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Voluntary
              </label>
            </div>
          </div>
        </div>

        {/* Materials Copay */}
        <div className="space-y-2">
          <Label htmlFor="materials-copay">Materials Copay</Label>
          <div className="flex gap-2">
            <Select value={value.materialsCopay} onValueChange={(v) => updateField('materialsCopay', v)}>
              <SelectTrigger id="materials-copay" className="w-[180px]">
                <SelectValue placeholder="Select copay amount" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">$0 (No Copay)</SelectItem>
                <SelectItem value="10">$10</SelectItem>
                <SelectItem value="15">$15</SelectItem>
                <SelectItem value="20">$20</SelectItem>
                <SelectItem value="25">$25</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {value.materialsCopay === 'other' && (
              <div className="flex items-center gap-1">
                <span className="text-sm">$</span>
                <Input
                  type="number"
                  placeholder="Amount"
                  className="w-24"
                  value={value.customCopayAmount || ''}
                  onChange={(e) => updateField('customCopayAmount', parseInt(e.target.value) || 0)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Benefits Frequency */}
        <div className="space-y-2">
          <Label htmlFor="benefits-frequency">Benefits Frequency</Label>
          <Select value={value.benefitsFrequency} onValueChange={(v) => updateField('benefitsFrequency', v)}>
            <SelectTrigger id="benefits-frequency">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="annual">Annual</SelectItem>
              <SelectItem value="biennial">Biennial (Every 2 Years)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Allowance Amount */}
        <div className="space-y-2">
          <Label htmlFor="allowance">Allowance Amount</Label>
          <Input
            id="allowance"
            type="number"
            placeholder="Enter allowance amount"
            value={value.allowanceAmount || ''}
            onChange={(e) => updateField('allowanceAmount', parseInt(e.target.value) || 0)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
